import { useState } from "react";
import { FiCheck, FiGrid, FiList } from "react-icons/fi";
import type { SortType } from "@/features/catalog/stores/catalogStore";
import { CATALOG_SORT_OPTIONS } from "@/features/catalog/utils/catalogSortOptions";
import { selectDesktopSort } from "@/features/catalog/utils/desktopCatalogSort";
import { Modal } from "@/shared/ui/overlay";

type CatalogDisplayType = "catalog" | "list";

interface CatalogSortModalProps {
  isOpen: boolean;
  initialDisplayType?: CatalogDisplayType;
  currentSort: SortType;
  selectSortMethod: (sort: SortType) => void;
  onShowCatalog: () => void;
  onShowList: () => void;
  onClose: () => void;
}

export function CatalogSortModal({
  isOpen,
  initialDisplayType = "catalog",
  currentSort,
  selectSortMethod,
  onShowCatalog,
  onShowList,
  onClose,
}: CatalogSortModalProps): React.ReactElement {
  const [displayType, setDisplayType] =
    useState<CatalogDisplayType>(initialDisplayType);

  // 並び順の選択を確定操作として扱い、保留中の表示タイプも同時に反映する。
  const applySelection = (selectedSort: SortType): void => {
    selectDesktopSort(currentSort, selectedSort, selectSortMethod);
    onClose();
    if (displayType === "catalog") {
      onShowCatalog();
    } else {
      onShowList();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="カタログソート"
      position="bottom"
      flickToClose={false}
    >
      <CatalogSortModalContent
        displayType={displayType}
        selectedSort={currentSort}
        onDisplayTypeChange={setDisplayType}
        onSortSelect={applySelection}
      />
    </Modal>
  );
}

interface CatalogSortModalContentProps {
  displayType: CatalogDisplayType;
  selectedSort: SortType;
  onDisplayTypeChange: (displayType: CatalogDisplayType) => void;
  onSortSelect: (sort: SortType) => void;
}

function CatalogSortModalContent({
  displayType,
  selectedSort,
  onDisplayTypeChange,
  onSortSelect,
}: CatalogSortModalContentProps): React.ReactElement {
  return (
    <section className="space-y-4 p-4" aria-label="カタログ表示設定">
      <fieldset className="m-0 border-0 p-0">
        <legend className="mb-2 text-sm font-semibold text-foreground">
          表示タイプ
        </legend>
        <fieldset
          aria-label="表示タイプ"
          className="history-mode-toggle catalog-sort-display-toggle max-w-none"
        >
          <button
            type="button"
            aria-pressed={displayType === "catalog"}
            className="inline-flex items-center justify-center gap-2"
            onClick={(): void => onDisplayTypeChange("catalog")}
          >
            <FiGrid aria-hidden="true" />
            カタログ
          </button>
          <button
            type="button"
            aria-pressed={displayType === "list"}
            className="inline-flex items-center justify-center gap-2"
            onClick={(): void => onDisplayTypeChange("list")}
          >
            <FiList aria-hidden="true" />
            リスト
          </button>
        </fieldset>
      </fieldset>

      <fieldset className="m-0 border-0 p-0">
        <legend className="mb-2 text-sm font-semibold text-foreground">
          並び順
        </legend>
        <ul className="m-0 list-none divide-y divide-border border-y border-border p-0">
          {CATALOG_SORT_OPTIONS.map(({ label, sort }) => (
            <li key={sort}>
              <button
                type="button"
                aria-pressed={selectedSort === sort}
                className="flex min-h-12 w-full items-center justify-between px-3 py-2 text-left font-medium text-foreground hover:bg-muted"
                onClick={(): void => onSortSelect(sort)}
              >
                {label}
                {selectedSort === sort && (
                  <FiCheck aria-hidden="true" className="text-primary" />
                )}
              </button>
            </li>
          ))}
        </ul>
      </fieldset>
    </section>
  );
}
